import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { set as storeSet } from "../lib/session_store"

export default class extends Controller {
  static values = {
    sessionId: String,
    live: Boolean,
  }

  static targets = [
    "fullscreenBtn",
    "expandIcon",
    "collapseIcon",
    "content",
    "trackMap",
    "container",
  ]

  connect() {
    this.latestState = null
    this.consumer = null
    this.subscription = null
    this.currentTrackedLap = null

    if (this.liveValue) {
      this.subscribe()
    }

    this.handleFullscreenChange = this.handleFullscreenChange.bind(this)
    document.addEventListener("fullscreenchange", this.handleFullscreenChange)
  }

  disconnect() {
    this.unsubscribe()
    document.removeEventListener(
      "fullscreenchange",
      this.handleFullscreenChange,
    )
  }

  subscribe() {
    this.consumer = createConsumer()
    this.subscription = this.consumer.subscriptions.create(
      { channel: "SessionChannel", session_id: this.sessionIdValue },
      {
        received: (data) => this.handleMessage(data),
      },
    )
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    if (this.consumer) {
      this.consumer.disconnect()
      this.consumer = null
    }
  }

  handleMessage(eventOrData) {
    // Accept both ActionCable data and Stimulus events (from replay controller)
    const data = eventOrData.detail || eventOrData

    switch (data.type) {
      case "race_state":
        this.latestState = data
        this.dispatch("update", { detail: data })
        this.checkLapChange(data)
        break
      case "lap_completed":
        this.dispatch("lap", { detail: data })
        break
      case "session_ended":
        this.liveValue = false
        this.unsubscribe()
        this.dispatch("ended")
        break
    }
  }

  toggleFullscreen() {
    const el = this.hasContainerTarget ? this.containerTarget : this.element
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      el.requestFullscreen()
    }
  }

  handleFullscreenChange() {
    const el = this.hasContainerTarget ? this.containerTarget : this.element
    const isFullscreen = document.fullscreenElement === el

    if (isFullscreen) {
      el.classList.add("fullscreen-pit-wall")
      el.classList.remove("rounded-lg")
    } else {
      el.classList.remove("fullscreen-pit-wall")
      el.classList.add("rounded-lg")
    }

    if (this.hasExpandIconTarget && this.hasCollapseIconTarget) {
      this.expandIconTarget.classList.toggle("hidden", isFullscreen)
      this.collapseIconTarget.classList.toggle("hidden", !isFullscreen)
    }
  }

  checkLapChange(data) {
    if (!data.cars) return

    const trackedIdx = data.tracked_car_index
    if (trackedIdx === undefined) return

    const trackedCar = data.cars.find((c) => c.car_index === trackedIdx)
    if (!trackedCar) return

    const lapNum = trackedCar.current_lap
    if (lapNum !== this.currentTrackedLap) {
      this.currentTrackedLap = lapNum
      const completedLap = lapNum - 1
      if (completedLap >= 1) {
        if (this.liveValue) {
          // Live: full navigation (Turbo Frame load, video seek, etc.)
          storeSet("activeLapNumber", completedLap)
        } else {
          // Replay: highlight only — avoid Turbo Frame reload and feedback loops
          this.dispatch("highlightLap", { detail: { lapNumber: completedLap } })
        }
      }
    }
  }

  get allCars() {
    if (!this.latestState || !this.latestState.cars) return []
    return [...this.latestState.cars].sort((a, b) => a.position - b.position)
  }
}
